import React from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";

import { V2 } from "../../Api";
import withTitle from "../withTitle";

import "../../blocks.css";
import "../../schedule.css";
import "../viewer.css";

interface ISurgeryManagementViewState {
    isLoading: boolean | string;

    devices: any[];
    drains: any[];
    surgeryTypes: any[];
    specimens: any[];
}

class SurgeryManagementView extends React.Component<RouteComponentProps<any>, ISurgeryManagementViewState> {
    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        this.state = {
            isLoading: true,

            devices: [],
            drains: [],
            specimens: [],
            surgeryTypes: [],
        };

        this.goAddSurgery = this.goAddSurgery.bind(this);
        this.goAddSpecimen = this.goAddSpecimen.bind(this);
        this.goAddDevice = this.goAddDevice.bind(this);
        this.goAddDrain = this.goAddDrain.bind(this);
    }

    public async componentDidMount() {
        try {
            const [devices, specimens, surgeryTypes, drains] = await Promise.all([
                V2.get<any[]>("glossary/devices"),
                V2.get<any[]>("glossary/specimens"),
                V2.get<any[]>("glossary/surgeries"),
                V2.get<any[]>("glossary/drains"),
            ]);

            this.setState({
                isLoading: false,

                devices,
                drains,
                specimens,
                surgeryTypes
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        if (typeof this.state.isLoading === "string") {
            return <div className="error-box">{this.state.isLoading}</div>;
        }

        if (this.state.isLoading) {
            return <div className="spinner spinner-center lds-dual-ring-big" />
        }

        return (
            <div className="viewer-root">
                <div className="column-root">
                    <div className="row-root">
                        <h3>Surgery Types</h3>
                        <table className="section-table table-vertical wrap align-top">
                            <thead>
                                <tr>
                                    <th className="col-smallest">Short Name</th>
                                    <th>Name</th>
                                    <th className="col-smallest col-num">Options</th>
                                    <th className="col-smallest" />
                                </tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr>
                                    <th>Surgery Types</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    this.state.surgeryTypes.length === 0
                                        ? <tr><td colSpan={10}>No surgery types found.</td></tr>
                                        : this.state.surgeryTypes.map(a => (
                                            <tr key={a.id}>
                                                <td>{a.shortName}</td>
                                                <td>{a.name}</td>
                                                <td data-caption="Options: " className="">{(a.options && a.options.length) || "None"}</td>
                                                <td><Link to={`/surgery/admin/surgerytype/${a.id}`} className="button btn-outline small">Edit</Link></td>
                                            </tr>
                                        ))
                                }
                            </tbody>
                        </table>
                        <div className="action-bar-dialog">
                            <button type="button" className="button btn-plus push-right" onClick={this.goAddSurgery}>Add Surgery Type</button>
                        </div>
                    </div>
                    <div className="row-root">
                        <h3>Specimens</h3>
                        <table className="section-table table-vertical wrap align-top">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th className="col-smallest th-font-tiny">Size</th>
                                    <th className="col-smallest th-font-tiny">Loc</th>
                                    <th className="col-smallest th-font-tiny">Weight</th>
                                    <th className="col-smallest" />
                                </tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr>
                                    <th>Specimens</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    this.state.specimens.length === 0
                                        ? <tr><td colSpan={10}>No specimens found.</td></tr>
                                        : this.state.specimens.map(a => (
                                            <tr key={a.id}>
                                                <td>{a.name}</td>
                                                <td className="col-center inline" data-caption="Size: ">{a.usesSize ? <i className="icon-check" /> : ""}</td>
                                                <td className="col-center inline" data-caption="Location: ">{a.usesLocation ? <i className="icon-check" /> : ""}</td>
                                                <td className="col-center inline" data-caption="Weight: ">{a.usesWeight ? <i className="icon-check" /> : ""}</td>
                                                <td><Link to={`/surgery/admin/specimen/${a.id}`} className="button btn-outline small">Edit</Link></td>
                                            </tr>
                                        ))
                                }
                            </tbody>
                        </table>
                        <div className="action-bar-dialog">
                            <button type="button" className="button btn-plus push-right" onClick={this.goAddSpecimen}>Add Specimen</button>
                        </div>
                    </div>
                </div>
                &nbsp;
                <div className="column-root">
                    <div className="row-root">
                        <h3>Devices</h3>
                        <table className="section-table table-vertical wrap align-top">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th className="col-smallest th-font-tiny">Lot</th>
                                    <th className="col-smallest th-font-tiny">Serial</th>
                                    <th className="col-smallest th-font-tiny">Size</th>
                                    <th className="col-smallest th-font-tiny">Exp</th>
                                    <th className="col-smallest" />
                                </tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr>
                                    <th>Devices</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    this.state.devices.length === 0
                                        ? <tr><td colSpan={10}>No devices found.</td></tr>
                                        : this.state.devices.map(a => (
                                            <tr key={a.id}>
                                                <td>{a.name}</td>
                                                <td className="col-center inline" data-caption="Lot: ">{a.usesLot ? <i className="icon-check" /> : ""}</td>
                                                <td className="col-center inline" data-caption="Serial: ">{a.usesSerial ? <i className="icon-check" /> : ""}</td>
                                                <td className="col-center inline" data-caption="Size: ">{a.usesSize ? <i className="icon-check" /> : ""}</td>
                                                <td className="col-center inline" data-caption="Expiration: ">{a.usesExpiration ? <i className="icon-check" /> : ""}</td>
                                                <td><Link to={`/surgery/admin/device/${a.id}`} className="button btn-outline small">Edit</Link></td>
                                            </tr>
                                        ))
                                }
                            </tbody>
                        </table>
                        <div className="action-bar-dialog">
                            <button type="button" className="button btn-plus push-right" onClick={this.goAddDevice}>Add Device</button>
                        </div>
                    </div>
                    <div className="row-root">
                        <h3>Drains & Packs</h3>
                        <table className="section-table table-vertical wrap align-top">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th className="col-smallest" />
                                </tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr>
                                    <th>Drains & Packs</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    this.state.drains.length === 0
                                        ? <tr><td colSpan={10}>No drains/packs found.</td></tr>
                                        : this.state.drains.map(a => (
                                            <tr key={a.id}>
                                                <td>{a.name}</td>
                                                <td><Link to={`/surgery/admin/drain/${a.id}`} className="button btn-outline small">Edit</Link></td>
                                            </tr>
                                        ))
                                }
                            </tbody>
                        </table>
                        <div className="action-bar-dialog">
                            <button type="button" className="button btn-plus push-right" onClick={this.goAddDrain}>Add Drain/Pack</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private goAddSurgery() {
        this.props.history.push("/surgery/admin/surgerytype/create");
    }

    private goAddSpecimen() {
        this.props.history.push("/surgery/admin/specimen/create");
    }

    private goAddDevice() {
        this.props.history.push("/surgery/admin/device/create");
    }

    private goAddDrain() {
        this.props.history.push("/surgery/admin/drain/create");
    }
}

export default withTitle("Surgery Management", SurgeryManagementView);
